import { ENDPOINTS_TAGS, rootApi } from 'src/v2/store/api/rootApi'

import {
  ICreateUserRequest,
  IUser,
  IUsersFilter,
  UsersResponseModel,
} from '../user.types'

export const userApi = rootApi.injectEndpoints({
  endpoints: (builder) => ({
    getUsers: builder.query<
      UsersResponseModel,
      IUsersFilter & { organizationId: string }
    >({
      query: (queryArgs) => ({
        url: '/v4/users',
        method: 'GET',
        params: {
          status: queryArgs.status,
          limit: queryArgs.limit,
          offset: queryArgs.offset,
          search: queryArgs.search,
          orderBy: queryArgs.orderBy,
          userType: queryArgs.userType,
          organizationRole: queryArgs.organizationRole,
        },
      }),
      providesTags: (result) =>
        result?.records.length
          ? [
              ...result.records.map(({ id }) => ({
                type: 'USERS' as const,
                id,
              })),
              'USERS',
            ]
          : [ENDPOINTS_TAGS.USERS],
    }),
    getUser: builder.query<IUser, IUser['id']>({
      query: (id) => ({
        url: `/v3/users/${id}`,
        method: 'GET',
      }),
      providesTags: (result) =>
        result
          ? [
              {
                type: 'GET_USER' as const,
                id: result.id,
              },
            ]
          : ['GET_USER'],
    }),
    updateUser: builder.mutation<
      Pick<
        IUser,
        | 'id'
        | 'firstName'
        | 'lastName'
        | 'phone'
        | 'username'
        | 'aircallUserId'
        | 'hipaaCertified'
        | 'type'
      >,
      Pick<
        IUser,
        | 'id'
        | 'firstName'
        | 'lastName'
        | 'phone'
        | 'username'
        | 'aircallUserId'
        | 'hipaaCertified'
        | 'password'
        | 'type'
      >
    >({
      query: (data) => {
        const {
          id,
          firstName,
          lastName,
          phone,
          username,
          aircallUserId,
          password,
          type,
        } = data
        return {
          url: `/users/${id}`,
          method: 'PATCH',
          data: {
            firstName,
            lastName,
            phone,
            email: username,
            aircallUserId: aircallUserId === '' ? null : aircallUserId,
            password: password === '' ? undefined : password,
            type,
          },
        }
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'USERS', id: arg.id },
        { type: 'GET_USER', id: arg.id },
      ],
    }),
    createUser: builder.mutation<Pick<IUser, 'id'>, ICreateUserRequest>({
      query: (data) => {
        const {
          firstName,
          lastName,
          phone,
          username,
          aircallUserId,
          password,
          organizationId,
          type,
        } = data
        return {
          url: `/users/`,
          method: 'POST',
          data: {
            firstName,
            lastName,
            phone,
            email: username,
            aircallUserId: aircallUserId === '' ? undefined : aircallUserId,
            password: password === '' ? undefined : password,
            organizationId,
            type,
          },
        }
      },
      invalidatesTags: ['USERS'],
    }),
    deleteUser: builder.mutation<{ ok: boolean }, IUser['id']>({
      query: (userId) => ({
        url: `/users/permanent-delete/${userId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['USERS'],
    }),
    autoGenerateEmail: builder.query({
      query: () => ({
        url: `/persons/generate-email/`,
        method: 'GET',
      }),
    }),
  }),
})

export const {
  useGetUsersQuery,
  useGetUserQuery,
  useUpdateUserMutation,
  useCreateUserMutation,
  useDeleteUserMutation,
  useAutoGenerateEmailQuery,
  endpoints: userApiEndpoints,
} = userApi
